import React from 'react';
import { ArrowLeft, Calculator, Info, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from 'react-i18next';

export default function TaxDocumentation() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/taxes" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('taxes.backToOverview', 'Back to Tax Overview')}
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3 flex items-center">
            <Calculator className="h-10 w-10 mr-3 text-emerald-600" />
            {t('taxes.howItWorks', 'How Does Tax Calculation Work?')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('taxes.explanation', 'Detailed explanation of our tax calculation methodology for Germany, Austria, and Switzerland')}
          </p>
        </div>

        {/* Disclaimer */}
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start">
              <AlertTriangle className="h-6 w-6 text-amber-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">Wichtiger Hinweis</h3>
                <p className="text-amber-800">
                  Diese Berechnungen sind <strong>vereinfacht</strong> und dienen nur zur <strong>groben Orientierung</strong>. 
                  Sie ersetzen <strong>KEINE professionelle Steuerberatung</strong>. Für verbindliche Steuererklärungen 
                  konsultieren Sie bitte einen Steuerberater oder das Finanzamt.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Germany Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              🇩🇪 Deutschland (2024)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">1. Einkommensteuer (§32a EStG)</h3>
              <p className="text-gray-700 mb-2">
                Die deutsche Einkommensteuer ist <strong>progressiv</strong> gestaltet. Der Steuersatz steigt mit dem Einkommen.
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Grundfreibetrag:</strong> 11.784 € (steuerfrei)</li>
                <li><strong>Eingangssteuersatz:</strong> 14% ab 11.785 €</li>
                <li><strong>Progressionszone 1:</strong> 14-24% bis 17.005 €</li>
                <li><strong>Progressionszone 2:</strong> 24-42% bis 66.760 €</li>
                <li><strong>Spitzensteuersatz:</strong> 42% bis 277.826 €</li>
                <li><strong>Reichensteuer:</strong> 45% über 277.826 €</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">2. Solidaritätszuschlag</h3>
              <p className="text-gray-700 mb-2">
                5,5% der Einkommensteuer, aber erst ab einer Einkommensteuer von <strong>19.950 €</strong> (Freigrenze 2024/2025).
                Ca. 90% der Steuerzahler zahlen keinen Soli mehr.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">3. Gewerbesteuer (nur für Gewerbetreibende!)</h3>
              <p className="text-gray-700 mb-2">
                <strong>WICHTIG:</strong> Freiberufler zahlen <strong>KEINE</strong> Gewerbesteuer!
              </p>
              <p className="text-gray-700 mb-2">Berechnung für Gewerbetreibende:</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Freibetrag:</strong> 24.500 € abziehen</li>
                <li><strong>Steuermesszahl:</strong> 3,5% auf den Rest</li>
                <li><strong>Hebesatz:</strong> × kommunaler Hebesatz (meist 350-450%)</li>
              </ol>
              <p className="text-gray-700 mt-2 text-sm italic">
                Beispiel: Bei 50.000 € Gewinn und 400% Hebesatz: (50.000 - 24.500) × 3,5% × 4 = 3.570 €
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">4. Umsatzsteuer (Mehrwertsteuer)</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Normalsatz:</strong> 19%</li>
                <li><strong>Ermäßigter Satz:</strong> 7% (Lebensmittel, Bücher, etc.)</li>
                <li><strong>Steuerfrei:</strong> 0% (bestimmte Dienstleistungen)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Austria Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              🇦🇹 Österreich (2025)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">1. Einkommensteuer</h3>
              <p className="text-gray-700 mb-2">
                Progressive Steuersätze in 7 Stufen:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Grundfreibetrag:</strong> 13.308 € (steuerfrei)</li>
                <li><strong>20%:</strong> 13.308 € - 21.617 €</li>
                <li><strong>30%:</strong> 21.617 € - 35.836 €</li>
                <li><strong>40%:</strong> 35.836 € - 69.166 €</li>
                <li><strong>48%:</strong> 69.166 € - 103.072 €</li>
                <li><strong>50%:</strong> 103.072 € - 1.000.000 €</li>
                <li><strong>55%:</strong> über 1.000.000 € (befristet bis 2025)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">2. Sozialversicherung (SVS) für Selbstständige</h3>
              <p className="text-gray-700 mb-2">
                Pflichtversicherung ab 6.613,20 € Jahreseinkommen:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Pensionsversicherung:</strong> 18,50%</li>
                <li><strong>Krankenversicherung:</strong> 6,80%</li>
                <li><strong>Selbständigenvorsorge:</strong> 1,53%</li>
                <li><strong>Unfallversicherung:</strong> 144,84 € pauschal/Jahr</li>
                <li><strong>Gesamt:</strong> ca. 26,83% + 144,84 €</li>
              </ul>
              <p className="text-gray-700 mt-2 text-sm">
                <strong>Höchstbeitragsgrundlage:</strong> 90.300 € (PV & KV)
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">3. Umsatzsteuer</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Normalsatz:</strong> 20%</li>
                <li><strong>Ermäßigter Satz:</strong> 13% (Lebensmittel, etc.)</li>
                <li><strong>Ermäßigter Satz:</strong> 10% (Bücher, Kultur, etc.)</li>
                <li><strong>Steuerfrei:</strong> 0%</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Switzerland Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              🇨🇭 Schweiz (2025)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">1. Bundessteuer (Direkte Bundessteuer)</h3>
              <p className="text-gray-700 mb-2">
                Progressive Bundessteuer mit Freibetrag von 17.800 CHF. Steuersätze von 0% bis 11%.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">2. Kantonale & Gemeindesteuern</h3>
              <p className="text-gray-700 mb-2">
                <strong>WICHTIG:</strong> Steuersätze variieren <strong>stark</strong> zwischen Kantonen!
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Niedrigste:</strong> Zug (ca. 8-12%)</li>
                <li><strong>Mittel:</strong> Zürich (ca. 13-18,5%)</li>
                <li><strong>Höchste:</strong> Genf (ca. 16-23%)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">3. Sozialversicherung (AHV/IV/EO)</h3>
              <p className="text-gray-700 mb-2">
                Für Selbstständige: ca. 5,371% - 10% je nach Einkommen
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">4. Mehrwertsteuer (MWST)</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>Normalsatz:</strong> 7,7%</li>
                <li><strong>Sondersatz Beherbergung:</strong> 3,7%</li>
                <li><strong>Reduzierter Satz:</strong> 2,5%</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* How We Calculate */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Info className="h-6 w-6 mr-2" />
              Wie berechnen wir?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">Unser Berechnungsprozess:</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4">
                <li>
                  <strong>Einnahmen sammeln:</strong> Alle Ihre eingetragenen Einnahmen werden summiert
                </li>
                <li>
                  <strong>Absetzbare Ausgaben abziehen:</strong> Nur als "steuerlich absetzbar" markierte Ausgaben
                </li>
                <li>
                  <strong>Zu versteuerndes Einkommen:</strong> Einnahmen - Ausgaben = Bemessungsgrundlage
                </li>
                <li>
                  <strong>Steuern berechnen:</strong> 
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>Einkommensteuer nach progressiver Formel</li>
                    <li>Sozialversicherung (AT/CH)</li>
                    <li>Solidaritätszuschlag (DE, falls über Freigrenze)</li>
                    <li>Gewerbesteuer (DE, nur für Gewerbetreibende)</li>
                  </ul>
                </li>
                <li>
                  <strong>Umsatzsteuer:</strong> Eingenommene USt - Bezahlte USt = USt-Zahllast
                </li>
              </ol>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
              <h4 className="font-semibold text-blue-900 mb-2">💡 Vereinfachungen in unserer Berechnung:</h4>
              <ul className="list-disc list-inside space-y-1 text-blue-800 text-sm ml-4">
                <li>Berechnung für <strong>Alleinstehende</strong> (keine Splittingtarife)</li>
                <li>Keine Berücksichtigung von Sonderausgaben, außergewöhnlichen Belastungen</li>
                <li>Keine Berücksichtigung von Kinderfreibeträgen</li>
                <li>Vereinfachte Gewerbesteuer (Standardhebesatz)</li>
                <li>Keine Berücksichtigung von Vorauszahlungen</li>
                <li>Schweiz: Vereinfachte Kantonssteuern (tatsächliche Steuern können abweichen)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-gray-600 text-sm mb-8">
          <p>
            <strong>Quellen:</strong> § 32a EStG (Deutschland), Österreichisches Einkommensteuergesetz (EStG), 
            SVS.at, Bundessteuergesetz Schweiz (DBG), Stand: 2024/2025
          </p>
          <p className="mt-2">
            Bei Fragen wenden Sie sich bitte an einen Steuerberater oder Ihr zuständiges Finanzamt.
          </p>
        </div>
      </div>
    </div>
  );
}
